/**
 * Vista imprimible del recibo de un pedido. Layout de ticket térmico
 * (80mm angosto cuando imprimas en POS, se adapta a A4 en browser).
 * Auto-print al cargar — el patrón es el mismo que CashReport.
 */

import { useEffect } from 'react';

import { formatCOP } from '../lib/format.js';
import { orderOriginLabels, orderStatusLabels } from '../lib/orders.js';
import { paymentMethodLabels } from '../lib/payments.js';
import { trpc } from '../lib/trpc.js';

import type { OrderOrigin, OrderStatus } from '@atreus/core';

const fmtDateTime = (ms: number | null): string =>
  ms == null
    ? '—'
    : new Date(ms).toLocaleString('es-CO', {
        dateStyle: 'short',
        timeStyle: 'short',
      });

interface ModifierEntry {
  name?: string;
  priceDeltaCents?: number;
}

const parseModifiers = (json: string): ModifierEntry[] => {
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as ModifierEntry[]) : [];
  } catch {
    return [];
  }
};

export const OrderReceiptPage = ({ orderId }: { orderId: string }) => {
  const data = trpc.orders.receiptData.useQuery({ id: orderId });

  useEffect(() => {
    if (!data.data) return;
    const t = setTimeout(() => window.print(), 500);
    return () => clearTimeout(t);
  }, [data.data]);

  if (data.isLoading) {
    return (
      <div className="p-8 text-fg-muted print:hidden">Cargando recibo…</div>
    );
  }
  if (data.error || !data.data) {
    return (
      <div className="p-8 text-danger print:hidden">
        {data.error?.message ?? 'No se encontró el pedido'}
      </div>
    );
  }

  const { tenant, order, items, payments, opener } = data.data;
  // `customer` solo existe cuando el pedido tiene fiado (worker ≥ 0011).
  const customer = data.data.customer ?? null;
  const orderOrigin = order.origin as OrderOrigin;
  const orderStatus = order.status as OrderStatus;
  const tableLabel =
    orderOrigin === 'table'
      ? (order.tableLabel ??
        (order.tableNumber != null ? String(order.tableNumber) : null))
      : null;

  const paidTotal = payments.reduce((acc, p) => acc + p.amountCents, 0);

  return (
    <div className="mx-auto max-w-sm p-6 font-mono text-sm text-fg print:max-w-none print:p-0 print:text-xs">
      {/* Encabezado */}
      <div className="text-center">
        <div className="text-base font-black tracking-tight">
          {tenant?.name ?? 'Atreus'}
        </div>
        {tenant?.legalName ? (
          <div className="text-xs">{tenant.legalName}</div>
        ) : null}
        {tenant?.nit ? (
          <div className="text-xs">
            NIT: {tenant.nit}
            {tenant.dv ? `-${tenant.dv}` : ''}
          </div>
        ) : null}
        {tenant?.municipality || tenant?.department ? (
          <div className="text-xs">
            {[tenant.municipality, tenant.department].filter(Boolean).join(', ')}
          </div>
        ) : null}
      </div>

      <Divider />

      {/* Datos del pedido */}
      <div className="space-y-0.5 text-xs">
        <KV k="Pedido">
          #{order.id.slice(-8).toUpperCase()}
        </KV>
        <KV k="Origen">
          {orderOriginLabels[orderOrigin]}
          {tableLabel ? ` · Mesa ${tableLabel}` : ''}
        </KV>
        <KV k="Estado">{orderStatusLabels[orderStatus]}</KV>
        <KV k="Abierto">{fmtDateTime(order.openedAt)}</KV>
        {order.closedAt ? (
          <KV k="Cerrado">{fmtDateTime(order.closedAt)}</KV>
        ) : null}
        {opener ? (
          <KV k="Atendido por">
            {opener.fullName?.trim() || opener.email}
          </KV>
        ) : null}
      </div>

      <Divider />

      {/* Items */}
      <div className="space-y-2">
        {items.length === 0 ? (
          <div className="text-center italic">Sin items</div>
        ) : (
          items.map((it) => {
            const modifiers = parseModifiers(it.modifiersJson);
            const lineTotal = it.qty * it.unitPriceCents;
            return (
              <div key={it.id}>
                <div className="flex justify-between gap-2">
                  <span className="flex-1 break-words">
                    {it.qty} × {it.productName ?? 'Producto'}
                  </span>
                  <span className="tabular-nums">{formatCOP(lineTotal)}</span>
                </div>
                {modifiers.length > 0 ? (
                  <ul className="ml-4 list-disc text-[11px] text-fg-muted">
                    {modifiers.map((m, i) => (
                      <li key={i}>
                        {m.name ?? ''}
                        {m.priceDeltaCents && m.priceDeltaCents !== 0
                          ? ` (${m.priceDeltaCents > 0 ? '+' : ''}${formatCOP(m.priceDeltaCents)})`
                          : ''}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {it.note ? (
                  <div className="ml-4 text-[11px] italic text-fg-muted">
                    {it.note}
                  </div>
                ) : null}
                <div className="text-[10px] text-fg-muted">
                  {it.productSku} · {formatCOP(it.unitPriceCents)} c/u
                </div>
              </div>
            );
          })
        )}
      </div>

      <Divider />

      {/* Totales */}
      <div className="space-y-0.5 text-xs">
        <Row label="Total productos" value={formatCOP(order.subtotalCents)} />
        {order.discountCents > 0 ? (
          <Row
            label="Descuento"
            value={`-${formatCOP(order.discountCents)}`}
          />
        ) : null}
        {/* Desglose tipo factura solo cuando hay IVA discriminado. */}
        {order.taxCents > 0 ? (
          <>
            <Row
              label="Base sin IVA"
              value={formatCOP(
                order.subtotalCents - order.discountCents - order.taxCents,
              )}
            />
            <Row label="IVA" value={formatCOP(order.taxCents)} />
          </>
        ) : null}
        {order.tipCents > 0 ? (
          <Row label="Propina" value={formatCOP(order.tipCents)} />
        ) : null}
        <div className="mt-1 flex justify-between border-t border-fg pt-1 text-sm font-black">
          <span>TOTAL</span>
          <span className="tabular-nums">{formatCOP(order.totalCents)}</span>
        </div>
      </div>

      {payments.length > 0 ? (
        <>
          <Divider />
          <div className="space-y-0.5 text-xs">
            <div className="font-bold">Pagos</div>
            {payments.map((p) => (
              <div key={p.id}>
                <Row
                  label={paymentMethodLabels[p.method] ?? p.method}
                  value={formatCOP(p.amountCents)}
                />
                {p.tenderedCents != null && p.tenderedCents > p.amountCents ? (
                  <>
                    <div className="flex justify-between gap-2 pl-3 text-fg-muted">
                      <span>Recibido</span>
                      <span className="tabular-nums">{formatCOP(p.tenderedCents)}</span>
                    </div>
                    <div className="flex justify-between gap-2 pl-3 font-bold">
                      <span>Cambio</span>
                      <span className="tabular-nums">
                        {formatCOP(p.tenderedCents - p.amountCents)}
                      </span>
                    </div>
                  </>
                ) : null}
              </div>
            ))}
            {paidTotal < order.totalCents ? (
              <Row
                label="Pendiente"
                value={formatCOP(order.totalCents - paidTotal)}
              />
            ) : null}
          </div>
        </>
      ) : null}

      {customer ? (
        <>
          <Divider />
          <div className="space-y-0.5 text-xs">
            <Row label="Cliente" value={customer.fullName} />
            {customer.phone ? <Row label="Teléfono" value={customer.phone} /> : null}
            <div className="flex justify-between gap-2 font-bold">
              <span>Saldo pendiente</span>
              <span className="tabular-nums">{formatCOP(customer.balanceCents)}</span>
            </div>
          </div>
        </>
      ) : null}

      <Divider />

      <div className="text-center text-[10px]">
        ¡Gracias por tu visita!
        <div className="mt-1 text-fg-muted">
          Atreus POS · {new Date().toLocaleString('es-CO')}
        </div>
      </div>

      {/* Botones solo en pantalla */}
      <div className="mt-6 flex justify-center gap-2 print:hidden">
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded-md border border-border bg-fg px-3 py-1.5 text-xs font-semibold text-fg-inverse hover:bg-fg-muted"
        >
          Imprimir
        </button>
        <button
          type="button"
          onClick={() => window.close()}
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-fg hover:bg-surface-muted"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
};

// ---------- Helpers ----------

const Divider = () => (
  <div className="my-2 border-t border-dashed border-fg-muted" />
);

const KV = ({ k, children }: { k: string; children: React.ReactNode }) => (
  <div className="flex justify-between gap-2">
    <span className="text-fg-muted">{k}</span>
    <span className="text-right">{children}</span>
  </div>
);

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between gap-2">
    <span>{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
);
